import React, { useEffect, useMemo, useState } from 'react';
import getRewardfulCoupon from '../lib/getRewardfulCoupon';
import useCheckoutCoupon from './useCheckoutCoupon';
import useCheckoutState from './useCheckoutState';
import getRewardfulReferral from '../lib/getRewardfulReferral';

const RewardfulContext = React.createContext();

function RewardfulProvider({ children }) {
  // only handle rewardful coupon once
  const [handledRewardfulCoupon, setHandledRewardfulCoupon] = useState(false);

  const { setShowCouponCodeField, query } = useCheckoutState();

  const { validateCoupon, getCouponByStripeCouponId } = useCheckoutCoupon();

  const rewardfulCoupon = getRewardfulCoupon();

  useEffect(() => {
    async function setCoupon(couponCode) {
      try {
        await validateCoupon(couponCode);
        setShowCouponCodeField(true);
      } catch (error) {
        // no-op
      }
    }

    async function getAndApplyCoupon(rewardfulCoupon) {
      try {
        const coupon = await getCouponByStripeCouponId(rewardfulCoupon.id);
        setCoupon(coupon.code);
      } catch (error) {
        // no-op
      }
    }

    if (rewardfulCoupon && handledRewardfulCoupon === false) {
      getAndApplyCoupon(rewardfulCoupon);
      setHandledRewardfulCoupon(true);
    }
  }, [
    rewardfulCoupon,
    handledRewardfulCoupon,
    getCouponByStripeCouponId,
    setShowCouponCodeField,
    validateCoupon,
  ]);

  const rewardfulReferral = useMemo(() => {
    const rewardfulReferralId = getRewardfulReferral();
    if (!rewardfulReferralId && query.rewardful_referral) {
      return query.rewardful_referral;
    }
    return rewardfulReferralId;
  }, [query]);

  return (
    <RewardfulContext.Provider
      value={{
        rewardfulCoupon,
        handledRewardfulCoupon,
        rewardfulReferral,
      }}
    >
      {children}
    </RewardfulContext.Provider>
  );
}

export default function useRewardful() {
  const context = React.useContext(RewardfulContext);

  if (context === undefined) {
    throw new Error('useRewardful must be used within a RewardfulProvider');
  }

  return context;
}

export { RewardfulProvider };
